import FacebookIcon from "@components/svgs/facebook-icon.svg";
import TwitterIcon from "@components/svgs/twitter-icon.svg";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import useAnalytics from "@hooks/useAnalytics";
import useClientContext from "@hooks/useClientContext";
import useGame from "@hooks/useGame";
import usePicksContext, { Slot, updatePicks } from "@hooks/usePicksContext";
import usePrevious from "@hooks/usePrevious";
import useSendPickMutation from "@hooks/useSendPickMutation";
import useSession from "@hooks/useSession";
import _cloneDeep from "lodash.clonedeep";
import React, { useEffect, useState } from "react";
import { publicBasePath } from "src/styles/styleUtils";
import settings from "../settings";
import DriverSlot from "./DriverSlot";
import {
  ColContainer,
  Connector,
  ControlBar,
  DriverList,
  LockMessage,
  NCSLogoArea,
  RaceList,
  ResultContainer,
  RoundFooter,
  RoundWrapper,
  SaveButton,
  ScoringButton,
  SeparatorSpan,
  TrophyArea,
} from "./RoundColumn.styled";
import TwelveSVG from "./svgs/12-round-marque.svg";
import SixteenSVG from "./svgs/16-round-marque.svg";
import FourSVG from "./svgs/4-round-marque.svg";
import EightSVG from "./svgs/8-round-marque.svg";
import UnlockSVG from "./svgs/lock-open.svg";
import LockSVG from "./svgs/lock.svg";
import NCSBadgeSVG from "./svgs/ncs-logo.svg";
import TrophySVG from "./svgs/ncs-trophy.svg";
const { API_BACKEND_HOSTNAME } = settings;

interface Props {
  roundId: number;
  isTabletCeil: boolean;
}
export type TCurrentView = "picking" | "scoring" | "viewing";

function RoundColumn({ roundId, isTabletCeil }: Props) {
  const { data: game } = useGame();
  const [{ currentEntry }] = useClientContext();
  const { data: session } = useSession();
  const [{ picks, needsToSave, focusedColumn }, picksDispatch] = usePicksContext();
  const [currentView, setCurrentView] = useState<TCurrentView>("picking");
  const { trackEvent } = useAnalytics();

  const isLoggedIn = Boolean(session.user.token);
  const entryId = currentEntry?.id;
  const round = game.rounds.find((r) => r.round_id === roundId);
  const nextRound = game.rounds.find((r) => r.round_id === roundId + 1);
  const raceNames = nextRound?.races?.map((id) => game?.races?.[id]?.track_name);

  const rounds = {
    1: "16",
    2: "12",
    3: "8",
    4: "4",
  };

  const qualifyingDrivers: Slot[] = game.drivers?.map((d, i) => ({
    id: `${i + 1}`,
    pick: d.driver_id,
  }));
  const results: Slot[] =
    game?.results?.rounds
      ?.find((r) => r.round_id === roundId)
      ?.drivers?.map((driver, index) => ({
        id: `${index + 1}`,
        pick: driver,
      })) || [];

  const now = Date.now();
  const isCurrentRound = round?.round_id === game.state?.current_round ?? false;
  const isPostLock = now > round?.locks_at ?? false;
  const isPreOpen = now < round?.starts_at ?? false;
  const isOpen = (!isPostLock && now >= round?.starts_at && isCurrentRound) ?? false;
  const isChampionshipRound = roundId === 4;

  const isCollapsed =
    isTabletCeil && (roundId === focusedColumn - 1 || (focusedColumn === 0 && roundId === 1));
  const isHidden =
    isTabletCeil &&
    (focusedColumn === 0 ? roundId > 1 : roundId > focusedColumn || roundId < focusedColumn - 1);

  const lastRaceId = round?.races?.[round?.races?.length - 1];
  const scoresAt = game?.races?.[lastRaceId]?.race_date; // The round scores on the last race's date (after the race finishes)

  // const now = Date.parse("10/5/2021 11:00 AM"); // mock a custom "now" date
  // const isOpen = (!isPostLock && now >= round?.starts_at) ?? false; // mock a custom "now" date

  /**
   * Format: Wednesday, September 1, 2021
   */
  const getLocaleString = (iso?: number): string =>
    new Date(iso || Date.now()).toLocaleString(["default"], {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "America/New_York",
    });

  /**
   * Format: Monday, Sep 20 @ 2:55 PM EDT
   */
  const getLockLocaleString = (iso?: number): string => {
    let commaPointer = 0;
    return new Date(iso || Date.now())
      .toLocaleString(["default"], {
        weekday: "long",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        timeZone: "America/New_York",
        timeZoneName: "short",
      })
      .replace(/,/g, (match) => {
        commaPointer++;
        return commaPointer === 2 ? " @" : match;
      });
  };
  const openLocaleString = getLocaleString(round?.starts_at);
  const lockLocaleString = getLockLocaleString(round?.locks_at);
  const scoresLocaleString = getLocaleString(scoresAt);

  const nextRoundIsOpen = (now >= nextRound?.starts_at && now < nextRound?.locks_at) ?? false;

  const hasScoring = Boolean(isPostLock && round.scoring_status === "final");

  const nullDrivers: Slot[] = [];
  for (let i = 0; i < (round?.slots ?? 16); i++) {
    nullDrivers.push({ id: `${i + 1}`, pick: null });
  }

  const drivers: Slot[] =
    (roundId === 0 ? qualifyingDrivers : hasScoring ? results : picks[roundId]) || nullDrivers;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor),
  );

  function onDragEnd(event: DragEndEvent) {
    // console.log("onDragEnd event:", event);
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = drivers.findIndex((item) => item.id === active.id);
      const newIndex = drivers.findIndex((item) => item.id === over.id);

      const newPicks = _cloneDeep(picks);
      newPicks[roundId] = arrayMove(drivers, oldIndex, newIndex);

      picksDispatch(updatePicks(newPicks));
      trackEvent("picks:make_pick_dnd");
    }
  }

  const { mutate, isError, isLoading, isSuccess, reset } = useSendPickMutation();
  const wasError = usePrevious(isError);

  // Reset the error message on next render cycle.
  useEffect(() => {
    if (wasError) reset();
  }, [reset, wasError]);

  useEffect(() => {
    setCurrentView("picking");
  }, [entryId]);

  return (
    !isHidden && (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={drivers} strategy={verticalListSortingStrategy}>
          <ColContainer isCollapsed={isCollapsed} roundId={roundId}>
            <RaceList roundId={roundId}>
              {isTabletCeil && (
                <>
                  {roundId === 0 && <SixteenSVG height="30" />}
                  {roundId === 1 && <TwelveSVG height="30" />}
                  {roundId === 2 && <EightSVG height="30" />}
                  {roundId === 3 && <FourSVG height="30" />}
                </>
              )}
              {!isCollapsed &&
                raceNames?.map((name, i) => (
                  <React.Fragment key={`${i}-name`}>
                    {name}
                    <SeparatorSpan />
                  </React.Fragment>
                ))}
            </RaceList>
            <RoundWrapper roundId={roundId}>
              {isChampionshipRound && (
                <TrophyArea>
                  <TrophySVG />
                </TrophyArea>
              )}
              <Connector isOpen={isOpen} roundId={roundId} isCurrentRound={isCurrentRound} />
              <DriverList roundId={roundId} isCurrentRound={isCurrentRound}>
                {roundId > 0 && (
                  <ControlBar>
                    {isOpen && (
                      <LockMessage isOpen={isOpen}>
                        <UnlockSVG width="12" height="12" alt="Unlocked icon" />
                        <span>Locks {lockLocaleString}</span>
                      </LockMessage>
                    )}
                    {isPreOpen && (
                      <LockMessage>
                        <LockSVG width="12" height="12" alt="Lock icon" />
                        <span>Opens {openLocaleString}</span>
                      </LockMessage>
                    )}
                    {isPostLock &&
                      (hasScoring ? (
                        <ResultContainer>
                          <div>Round of {round?.slots} Results</div>
                          {currentView === "picking" ? (
                            <ScoringButton onClick={() => setCurrentView("scoring")}>
                              Show Scoring
                            </ScoringButton>
                          ) : (
                            <ScoringButton onClick={() => setCurrentView("picking")}>
                              Hide Scoring
                            </ScoringButton>
                          )}
                        </ResultContainer>
                      ) : (
                        <LockMessage>
                          <LockSVG width="12" height="12" alt="Lock" />
                          <span>
                            Locked. Scores{" "}
                            {scoresLocaleString.substring(scoresLocaleString.indexOf(",") + 1)}
                          </span>
                        </LockMessage>
                      ))}
                  </ControlBar>
                )}
                {drivers?.map((slot, index) => {
                  const resultPts =
                    currentEntry?.results?.rounds?.[roundId - 1]?.total_points?.[index] ?? null;

                  return (
                    <DriverSlot
                      key={slot.id}
                      id={slot.id}
                      slotNumber={index + 1}
                      driverId={slot.pick}
                      currentView={currentView}
                      isEditable={isOpen}
                      isPickable={nextRoundIsOpen}
                      hasScoring={hasScoring}
                      points={resultPts}
                      roundId={roundId}
                      isCollapsed={isCollapsed}
                    />
                  );
                })}
              </DriverList>
              {isChampionshipRound && (
                <NCSLogoArea>
                  <NCSBadgeSVG />
                </NCSLogoArea>
              )}
            </RoundWrapper>
            <RoundFooter>
              <div className="round-save">
                {roundId === 0 && <SixteenSVG width="58" />}
                {roundId === 1 && <TwelveSVG width="58" />}
                {roundId === 2 && <EightSVG width="58" />}
                {roundId === 3 && <FourSVG width="58" />}
                {isOpen && (
                  <SaveButton
                    disabled={!needsToSave || isLoading}
                    onClick={() => mutate(null)}
                    isError={isError}
                    isSuccess={isSuccess}
                    needsToSave={needsToSave}
                  >
                    {isLoading ? "Saving..." : isSuccess && !needsToSave ? "Saved!" : "Save Picks"}
                    <span className="messageArea">
                      {isError && "Something went wrong. Try again."}
                    </span>
                  </SaveButton>
                )}
              </div>
              {isOpen &&
                !needsToSave &&
                isLoggedIn &&
                drivers.every((slot) => typeof slot.pick === "number") && (
                  <div className="social-share">
                    <p>Share My Top Pick!</p>
                    <div>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${API_BACKEND_HOSTNAME}${publicBasePath}/entry/${entryId}/round/${
                          rounds[`${round.round_id}`]
                        }/driver/${picks[roundId][0].pick}`}
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        <FacebookIcon />
                      </a>
                      <a
                        href={`https://twitter.com/intent/tweet?url=${API_BACKEND_HOSTNAME}${publicBasePath}/entry/${entryId}/round/${
                          rounds[`${round.round_id}`]
                        }/driver/${picks[roundId][0].pick}`}
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        <TwitterIcon />
                      </a>
                    </div>
                  </div>
                )}
            </RoundFooter>
          </ColContainer>
        </SortableContext>
      </DndContext>
    )
  );
}

export default RoundColumn;
